import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { FileText, Shield, Brain, Database, Building, Clock, TrendingUp, CheckCircle, Users } from "lucide-react";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "What is tender management software?", answer: "Tender management software gives bid teams a single workspace to extract requirements, draft responses, track compliance, and submit competitive tenders — replacing Word, email, and shared drives." },
  { question: "Is tender management software the same as bid management software?", answer: "They overlap heavily. 'Tender' is the common term in public sector procurement worldwide; 'bid' is broader and used across commercial and public bids. Sorvaine handles both." },
  { question: "Does it work for public sector tenders?", answer: "Yes. Sorvaine maps each mandatory requirement and surfaces compliance gaps before submission — critical for public sector tenders where a single missed item disqualifies the response." },
  { question: "Can multiple contributors collaborate on the same tender?", answer: "Yes. Sorvaine assigns sections to owners, tracks status live, and lets reviewers comment in context without juggling document versions." },
  { question: "How fast can we go from tender document to first draft?", answer: "Minutes. Upload the tender, Sorvaine extracts the requirements and produces a structured outline with AI-drafted answers using your knowledge vault." },
  { question: "Does Sorvaine support framework and ITT submissions?", answer: "Yes. Any structured procurement document — RFPs, RFIs, ITTs, DDQs, and framework agreements — works the same way." },
];

export default function TenderManagementSoftware() {
  return (
    <ContentPageLayout
      title="Tender Management Software — AI Tender Platform | Sorvaine"
      description="Sorvaine is AI tender management software that turns tender documents into submission-ready responses. Extract requirements, draft answers, and track compliance in one workspace."
      canonical={`${BASE}/tender-management-software`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "Tender Management Software", url: `${BASE}/tender-management-software` },
      ]}
      ctaHeading="See Sorvaine in action"
      ctaDescription="Book a 20-minute demo and see how your team can go from tender document to first draft in minutes."
    >
      <SchemaMarkup type="software" name="Sorvaine" description="AI tender management software for teams responding to public and private sector tenders." url={`${BASE}/tender-management-software`} category="BusinessApplication" />
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        Tender Management Software
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[620px]">
        Sorvaine reads your tender, extracts every requirement, and generates a structured first draft — so your team focuses on winning, not formatting.
      </p>

      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">The tender problem</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Tenders pile up. Each one is hundreds of pages, dozens of mandatory requirements, and a tight clock. Most teams burn the first week reading and the last week reformatting — which is why winnable tenders get dropped or shipped under-baked.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">How Sorvaine solves it</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Upload the tender document. Sorvaine extracts every question and criterion, builds a section-by-section outline, drafts each answer using your knowledge vault, and tracks compliance live. You spend the time where it matters — on win themes and pricing.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Built for tender teams</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {[
            { icon: FileText, title: "Requirement extraction", desc: "Pulls every question, criterion, and mandatory item from the tender document automatically." },
            { icon: Shield, title: "Compliance mapping", desc: "Maps your draft to each requirement live — so nothing slips through review." },
            { icon: Brain, title: "AI answer drafting", desc: "Generates first drafts in your tone of voice using past responses and source material." },
            { icon: Database, title: "Self-building knowledge vault", desc: "Every completed tender feeds your library — searchable, reusable, and stronger with each submission." },
          ].map((f) => (
            <div key={f.title} className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <f.icon className="w-5 h-5 text-primary mb-3" />
              <h3 className="text-sm font-semibold text-white/80">{f.title}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Who uses Sorvaine</h2>
        <div className="mt-6 space-y-5">
          {[
            { icon: Building, title: "Public sector suppliers", desc: "Firms responding to government tenders worldwide where compliance is non-negotiable." },
            { icon: Users, title: "Consultancies and agencies", desc: "Bid teams pitching for advisory, implementation, and managed service contracts." },
            { icon: FileText, title: "Construction and engineering", desc: "Teams managing ITTs and framework submissions across multiple frameworks at once." },
          ].map((u) => (
            <div key={u.title} className="flex gap-4 items-start">
              <u.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-white/80">{u.title}</h3>
                <p className="mt-1 text-sm text-white/40">{u.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Why teams switch</h2>
        <div className="mt-6 space-y-4">
          {[
            { icon: Clock, text: "Cut tender response time by 40–60%. Bid on more opportunities without growing the team." },
            { icon: TrendingUp, text: "Win more by shipping consistent, on-message tenders on every submission." },
            { icon: CheckCircle, text: "Never miss a mandatory requirement — automated compliance tracking catches what humans overlook." },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <b.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-white/50">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Compare Sorvaine</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/bid-management-software" className="text-sm text-primary hover:underline">Bid Management Software →</Link>
          <Link to="/loopio-alternatives" className="text-sm text-primary hover:underline">Sorvaine vs Loopio →</Link>
          <Link to="/rfpio-alternatives" className="text-sm text-primary hover:underline">Sorvaine vs RFPIO →</Link>
          <Link to="/best-rfp-software" className="text-sm text-primary hover:underline">Best RFP Software 2026 →</Link>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Learn more</h2>
        <ul className="mt-4 space-y-2">
          <li><Link to="/resources/what-is-rfp-response-software" className="text-sm text-primary hover:underline">What Is RFP Response Software?</Link></li>
          <li><Link to="/resources/how-to-respond-to-an-rfp" className="text-sm text-primary hover:underline">How to Respond to a Tender: Step by Step</Link></li>
          <li><Link to="/resources/common-rfp-mistakes" className="text-sm text-primary hover:underline">7 Common Tender Mistakes &amp; How to Fix Them</Link></li>
        </ul>
      </section>
    </ContentPageLayout>
  );
}
